import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { toast } from 'react-toastify';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { clientActions, goalActions, httpRequestsOnSuccessActions, sessionActions } from 'store';
import { FindLoad, FindSuccessItem, Images, renderClientName, useModal } from 'utils';
import { CustomDelete, Loader, MinLoader, NoItemsYet, NotError } from 'components';
import { SessionFilters } from '../session/core';
import { CreateSession } from '../session/createSession';
import { ManualGoalList } from './manualGoalList';
import { ManualPinnedTargets } from './manualPinnedTargets';
import { useManualGoalFilters } from './useManualGoalFilters';
import {
    apptTimeRange,
    buildManualPayloadData,
    findEntryProblem,
    GOALS_FOR_SESSION_ACTION,
    hasEntryChanges,
    MANUAL_SESSION_LOAD_ACTION,
    MANUAL_SESSION_SAVE_ACTION,
    manualQueryKeys,
    toIsoDateTime,
    trialsFromTSummary,
} from './constants';

const CLIENT_GOALS_TAB = 'Goals';

/**
 * Manual session data entry screen — `/client/:id/manual-data?date&time&end&appt&session`.
 *
 * Looks like the live session screen but owns its list: search + `SessionFilters`,
 * `useManualGoalFilters` and `ManualGoalList` with `ManualTargetRow` as the target card. The live
 * session code (`session.js`, `SessionGoalListTargets`, the session cards) is untouched. Shows the goals picked
 * in Session Preparation (all eligible goals after a reload, since the selection travels in router
 * state) plus any goal that already holds data on the session being edited. Pinned goals come first. Save sends one `PUT /clinical/sessions/manual` with only the targets that changed; the
 * backend completes the session and runs mastery, as when a live session ends.
 *
 * Edit Goals reopens Session Preparation on the current selection and writes the result back into router
 * state (a `replace` navigation — the screen doesn't remount, typed data stays). Goals holding entered data are
 * locked, so no data can be dropped by deselecting its goal.
 */
export const ManualSession = () => {
    const { id: clientId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { open, close } = useModal();
    const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const date = query.get(manualQueryKeys.DATE);
    const time = query.get(manualQueryKeys.TIME);
    const endTime = query.get(manualQueryKeys.END_TIME);
    const apptId = query.get(manualQueryKeys.APPT);
    const sessionId = query.get(manualQueryKeys.SESSION);
    /** `[{ goal, pinned }]` from Session Preparation, in the picked order. Lost on reload → every eligible goal. */
    const pickedGoals = location.state?.goals;

    const client = useSelector((state) => state.client.clientItemInfo);
    const goalsForSession = useSelector((state) => state.goals.goalsForSession);
    const manualSession = useSelector((state) => state.session.manualSession);
    const appts = useSelector((state) => state.session.manualSessionAppts);
    const loadGoals = FindLoad(GOALS_FOR_SESSION_ACTION);
    const loadSession = FindLoad(MANUAL_SESSION_LOAD_ACTION);
    const saving = FindLoad(MANUAL_SESSION_SAVE_ACTION);
    const saveSuccess = FindSuccessItem(MANUAL_SESSION_SAVE_ACTION);

    const [entries, setEntries] = useState({});
    const [initial, setInitial] = useState({});
    const [ready, setReady] = useState(false);
    const [filters, setFilters] = useState({});
    const [collapsed, setCollapsed] = useState(() => new Set());
    // `{ targetId, trialIndex }` of the trial Save rejected — that trial's inputs show the error.
    const [entryError, setEntryError] = useState(null);
    const seededFor = useRef(null);
    const loaded = useRef(false);

    const sessionLoaded = !sessionId || manualSession?.id === sessionId;
    const appt = appts?.find((a) => a?.id === apptId) || (manualSession?.appt?.id === apptId ? manualSession?.appt : null);

    useEffect(() => {
        if (!date) {
            navigate(`/client/${clientId}`, { replace: true, state: { activeTab: CLIENT_GOALS_TAB } });
            return;
        }
        dispatch(goalActions.getGoalsForSession(clientId));
        if (client?.id !== clientId) dispatch(clientActions.getClientsById(clientId));
        if (apptId) dispatch(sessionActions.getManualSessionAppts({ clientId, date }));
        if (sessionId && manualSession?.id !== sessionId) dispatch(sessionActions.getManualSession(sessionId));
    }, [clientId, date, apptId, sessionId]);

    useEffect(() => () => dispatch(sessionActions.resetManualSession()), []);

    /** Trials already on the session being edited, keyed by target id. */
    const sessionTrials = useMemo(() => {
        const byTarget = {};
        if (!sessionId || !sessionLoaded) return byTarget;
        (manualSession?.goals || []).forEach((g) =>
            (g?.goal?.targets || []).forEach((t) => {
                const trials = trialsFromTSummary(t?.tSummary);
                if (trials.length) byTarget[t.id] = trials;
            })
        );
        return byTarget;
    }, [manualSession, sessionId, sessionLoaded]);

    // Seed once per session: re-seeding on a later refetch would wipe what the user typed.
    useEffect(() => {
        const key = sessionId || 'new';
        if (!sessionLoaded || seededFor.current === key) return;
        seededFor.current = key;
        setEntries(sessionTrials);
        setInitial(sessionTrials);
        setReady(true);
    }, [sessionLoaded, sessionTrials, sessionId]);

    const goals = useMemo(() => {
        const withData = new Set(
            (manualSession?.goals || [])
                .filter((g) => g?.goal?.targets?.some((t) => sessionTrials[t?.id]))
                .map((g) => g?.goal?.id)
        );
        const all = goalsForSession || [];
        if (!pickedGoals?.length) return all;
        const byId = new Map(all.map((g) => [g?.id, g]));
        const picked = pickedGoals.map((p) => byId.get(p?.goal)).filter(Boolean);
        const pickedIds = new Set(picked.map((g) => g.id));
        return [...picked, ...all.filter((g) => !pickedIds.has(g?.id) && withData.has(g?.id))];
    }, [goalsForSession, pickedGoals, manualSession, sessionTrials]);

    const pinnedIds = useMemo(() => new Set((pickedGoals || []).filter((p) => p?.pinned).map((p) => p?.goal)), [pickedGoals]);

    // The `[{ goal, pinned }]` shape the live screen uses: pinned goals go to the strip on top
    // (`ManualPinnedTargets`), the rest to the list (`ManualGoalList`).
    const sessionGoals = useMemo(() => goals.map((goal) => ({ goal, pinned: pinnedIds.has(goal.id) })), [goals, pinnedIds]);

    const visibleGoals = useManualGoalFilters(sessionGoals, filters);

    const dirty = hasEntryChanges(entries, initial);

    useEffect(() => {
        if (!saveSuccess) return;
        dispatch(httpRequestsOnSuccessActions.removeSuccess(MANUAL_SESSION_SAVE_ACTION));
        navigate(`/client/${clientId}`, { state: { activeTab: CLIENT_GOALS_TAB } });
    }, [saveSuccess]);

    // A reload or tab close would lose everything typed — let the browser ask.
    useEffect(() => {
        if (!dirty) return undefined;
        const warn = (e) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [dirty]);

    /**
     * Goals with data — typed, or saved on the session (even if cleared since: the save must still send the
     * removal) — can't be deselected in Edit Goals.
     */
    const lockedGoalIds = useMemo(
        () => goals.filter((g) => g?.targets?.some((t) => entries[t?.id]?.length || initial[t?.id]?.length)).map((g) => g.id),
        [goals, entries, initial]
    );

    const handleEditGoals = () =>
        open(
            <CreateSession
                clientId={clientId}
                initialGoals={sessionGoals.map((g) => ({ goal: g.goal.id, pinned: g.pinned }))}
                lockedGoalIds={lockedGoalIds}
                title="Edit Session Goals"
                confirmText="Update Goals"
                onCancel={close}
                onConfirm={(picked) => {
                    close();
                    navigate(`${location.pathname}${location.search}`, { replace: true, state: { ...location.state, goals: picked } });
                }}
            />
        );

    const leave = () => navigate(`/client/${clientId}`, { state: { activeTab: CLIENT_GOALS_TAB } });

    const handleLeave = () => {
        if (!dirty) return leave();
        open(
            <CustomDelete
                info="Discard Manual Data?"
                text="You have entered data. If you leave now, all data entered in this manual entry will be lost."
                innerText="Discard Data"
                handleClose={close}
                handleDel={() => {
                    close();
                    leave();
                }}
            />
        );
    };

    const handleSave = () => {
        const problem = findEntryProblem(goals, entries);
        if (problem) {
            setEntryError({ targetId: problem.targetId, trialIndex: problem.trialIndex });
            // A missing input marked red says what's wrong; the toast covers problems with no field to mark or a wrong value.
            if (!problem.fields.length || problem.explain) toast(<NotError type="error" errMessage={problem.message} />);
            (document.getElementById(`manual-target-${problem.targetId}`) || document.getElementById(`manual-pinned-${problem.targetId}`))?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }
        setEntryError(null);

        const data = buildManualPayloadData(goals, entries, initial, date);
        if (!data.length) return;

        // Ignored by the backend when an appointment is linked — it uses the appointment's times.
        const sessionDate = toIsoDateTime(date, time || moment().format('HH:mm:ss'));
        const sessionEndDate = endTime ? toIsoDateTime(date, endTime) : null;
        if (!apptId && moment(sessionEndDate || sessionDate).isAfter(moment())) {
            toast(<NotError type="error" errMessage="The session time is in the future. Pick an earlier time." />);
            return;
        }

        const body = { clientId, date: sessionDate, data };
        if (!apptId && sessionEndDate) body.endDate = sessionEndDate;
        if (apptId) body.apptId = apptId;
        if (sessionId) body.sessionId = sessionId;
        dispatch(sessionActions.saveManualSession(body));
    };

    const setTargetTrials = (targetId, trials) => {
        if (targetId === entryError?.targetId) setEntryError(null);
        setEntries((prev) => ({ ...prev, [targetId]: trials }));
    };

    // Collapse / Expand all drives the list only — pinned goals sit in the strip.
    const listGoals = visibleGoals.filter((g) => !g?.pinned);
    const anyExpanded = listGoals.some((g) => !collapsed.has(g?.goal?.id));
    const toggleAll = () => setCollapsed(anyExpanded ? new Set(listGoals.map((g) => g?.goal?.id)) : new Set());
    const toggleGoal = (goalId) =>
        setCollapsed((prev) => {
            const next = new Set(prev);
            next.has(goalId) ? next.delete(goalId) : next.add(goalId);
            return next;
        });

    const dateLabel = date ? moment(date).format('MM/DD/YYYY') : '';
    const linkLabel = apptId
        ? `Linked to appointment ${appt?.displayId || ''}${appt ? ` · ${apptTimeRange(appt)}` : ''}`
        : `No appointment linked${time ? ` · ${[time, endTime].filter(Boolean).map((t) => moment(t, 'HH:mm:ss').format('hh:mm A')).join(' – ')}` : ''}`;

    // The loader is for the first load only: Edit Goals refetches the goals, and swapping the page for a loader
    // then would reset every row's trial position.
    if (ready && !loadGoals?.length && !loadSession?.length) loaded.current = true;
    if (!loaded.current) {
        return (
            <div className="session-page-wrapper">
                <Loader height={'90vh'} />
            </div>
        );
    }

    return (
        <div className="session-page-wrapper manual-session-page">
            <div className="session-header-box">
                <div className="session-back-btn-box">
                    <button onClick={handleLeave} aria-label="Back to goals">
                        <img src={Images.backArrow} alt="back" />
                    </button>
                    <p>{`${renderClientName(client?.id === clientId ? client : null)}, ${dateLabel}`}</p>
                </div>

                <div className="flex-align-center" style={{ gap: 10 }}>
                    <div className="search-for-goal-box">
                        <input
                            type="text"
                            placeholder="Search for goals.."
                            value={filters.search || ''}
                            onChange={(e) => {
                                const next = { ...filters };
                                if (e.target.value === '') delete next.search;
                                else next.search = e.target.value;
                                setFilters(next);
                            }}
                        />
                        <button type="button" tabIndex={-1}>
                            <img src={Images.searchIcon} alt="search" />
                        </button>
                    </div>
                    <SessionFilters saveFilters={setFilters} savedFilters={filters} />
                    <button type="button" style={{ width: '130px', gap: 8 }} onClick={handleEditGoals} className="session-gray-action-btn-box">
                        <img src={Images.sessionEditGoal} alt="" />
                        <p>Edit Goals</p>
                    </button>
                </div>
            </div>

            <div className="manual-session-infobar">
                <span className="manual-session-infobar-title">Manual Data Entry</span>
                <span className="manual-session-infobar-divider" />
                <span>
                    {`${dateLabel} · ${linkLabel}`}
                    {sessionId && ' · Editing saved data'}
                </span>
            </div>

            <ManualPinnedTargets goals={visibleGoals} clientId={clientId} entries={entries} entryError={entryError} onTargetChange={setTargetTrials} />

            {!!listGoals.length && (
                <div className="flex-end" style={{ padding: '0 16px' }}>
                    <button type="button" className="goal-toggle-all-btn" onClick={toggleAll}>
                        {anyExpanded ? <UnfoldLessIcon sx={{ fontSize: 18 }} /> : <UnfoldMoreIcon sx={{ fontSize: 18 }} />}
                        {anyExpanded ? 'Collapse all' : 'Expand all'}
                    </button>
                </div>
            )}

            <div style={{ flex: 1 }}>
                {visibleGoals.length ? (
                    <ManualGoalList
                        goals={visibleGoals}
                        clientId={clientId}
                        collapsedGoalIds={collapsed}
                        onToggleGoal={toggleGoal}
                        entries={entries}
                        entryError={entryError}
                        onTargetChange={setTargetTrials}
                    />
                ) : (
                    <NoItemsYet
                        text={filters.search ? 'No goals match your search' : 'No active goals with targets to collect data on'}
                        image={Images.noClientYet}
                    />
                )}
            </div>

            <div className="manual-session-footer">
                <button type="button" className="close-btn-box" onClick={handleLeave}>
                    Cancel
                </button>
                <button
                    type="button"
                    className="confirm-btn"
                    disabled={!dirty || !!saving?.length}
                    style={{ opacity: !dirty ? 0.5 : 1 }}
                    onClick={handleSave}
                >
                    {saving?.length ? <MinLoader margin={'0'} color={'#FFF'} /> : 'Save Data'}
                </button>
            </div>
        </div>
    );
};
